import { getAccountId } from '@seek/adv-account-selection';
import type { JobDetailsView } from '@seek/job-details-view';
import { useTranslations } from '@vocab/react';
import { Box, Loader } from 'braid-design-system';
import type { ComponentProps } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { JobPreview } from 'src/app/shared/components/JobPreview';
import type { SearchResultPreview } from 'src/app/shared/components/JobPreview/components/SearchResultPreview';
import {
  PREVIEW_JOB_DETAILS_PRESSED,
  PREVIEW_PRINT_JOB_DETAILS_PRESSED,
  PREVIEW_PRINT_SEARCH_RESULTS_PRESSED,
  PREVIEW_SEARCH_RESULT_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { isANZCountryCode } from 'src/app/utils/country';
import { getSupportedCategoryCountry } from 'src/app/utils/getCategoryCountry';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import { log } from 'src/app/utils/logging';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useDraft } from 'src/app/v2/hooks/useDraft';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { getCreateGuidedJobDescription } from 'src/app/v2/utils/getCreateGuidedJobDescription';
import { getDeriveWorkType } from 'src/app/v2/utils/getDeriveWorkType';
import { getJobDetailsForPreview } from 'src/app/v2/utils/getJobDetailsForPreview';
import { getSerpDetailsForPreview } from 'src/app/v2/utils/getSerpDetailsForPreview';
import {
  useGetBrandQuery,
  useGetLocationQuery,
  useGetQuestionsQuery,
  useGetSuggestedCategoriesQuery,
} from 'src/graphql/generated';

import translations from './.vocab';

export const PreviewSection: React.FC = () => {
  const draft = useDraft();
  const { trackingAdapter } = useAnalyticsTracking();
  const { cartFeatures } = useShoppingCart();
  const { getLanguage } = useConfig();
  const hirerAccount = useHirerAccount();

  const { t } = useTranslations(translations);
  const adcentreAdvertiserId = getAccountId();

  // Disable this lint rule because these queries will be skipped if input is not available
  // but typescript does not type narrow based on the input
  /* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
  const { data: { questionnaire } = {}, loading: questionsLoading } =
    useGetQuestionsQuery({
      variables: {
        questionnaireId: draft?.questionnaireId!,
        advertiserId: adcentreAdvertiserId!,
      },
      skip: !draft?.questionnaireId || !adcentreAdvertiserId,
    });

  const { data: { location } = {}, loading: locationLoading } =
    useGetLocationQuery({
      variables: {
        locationId: draft?.locationsV2?.[0]?.id!,
        contextualCountry: hirerAccount?.nation,
        language: getLanguage(),
      },
      fetchPolicy: 'cache-first',
      skip: !draft?.locationsV2?.[0]?.id,
    });

  const { data: { brand } = {}, loading: brandLoading } = useGetBrandQuery({
    variables: {
      id: draft?.branding?.id!,
    },
    fetchPolicy: 'cache-first',
    skip: !draft?.branding?.id,
  });

  const { data: { suggestedCategories } = {}, loading: categoriesLoading } =
    useGetSuggestedCategoriesQuery({
      variables: {
        jobTitle: draft?.titles?.[0]?.title!,
        country: getSupportedCategoryCountry(location?.country || ''),
      },
      fetchPolicy: 'cache-first',
      skip: !draft?.titles?.[0]?.title || !location?.country,
    });
  /* eslint-enable @typescript-eslint/no-non-null-asserted-optional-chain */

  const handleTabClick = (index: number) => {
    const eventData = {
      eventName: Boolean(index)
        ? PREVIEW_SEARCH_RESULT_PRESSED
        : PREVIEW_JOB_DETAILS_PRESSED,
    };
    trackingAdapter.sendLinkEvent(eventData);
  };

  const handlePrintClick = (index: number) => {
    const eventData = {
      eventName: Boolean(index)
        ? PREVIEW_PRINT_SEARCH_RESULTS_PRESSED
        : PREVIEW_PRINT_JOB_DETAILS_PRESSED,
    };
    trackingAdapter.sendLinkEvent(eventData);
  };

  const createGuidedJobDescription = getCreateGuidedJobDescription(
    t('preview-guided-about-title'),
    t('preview-guided-responsibilities-title'),
    t('preview-guided-qualifications-title'),
    t('preview-guided-benefits-title'),
  );

  const deriveWorkType = getDeriveWorkType({
    fullTimeLabel: t('work-type-taxonomy-full-time'),
    partTimeLabel: t('work-type-taxonomy-part-time'),
    contractLabel: t('work-type-taxonomy-contract'),
    casualLabel: t('work-type-taxonomy-casual'),
  });

  return (
    <PreviewSectionView
      handlePrintClick={handlePrintClick}
      handleTabClick={handleTabClick}
      loading={
        locationLoading || brandLoading || categoriesLoading || questionsLoading
      }
      jobDetails={getJobDetailsForPreview({
        translatedLabels: {
          privateCompanyLabel: t('preview-private-advertiser'),
          hiddenSalaryLabel: t('preview-hidden-salary-text'),
          postedDate: t('preview-posted-date'),
          workType: deriveWorkType(draft?.workTypes?.[0]),
        },
        draft,
        location,
        categories: suggestedCategories,
        brand,
        hirerAccount,
        selectedRRQuestions: questionnaire?.questions?.map((q) => q.text),
        guidedJobDescription: createGuidedJobDescription(draft?.guidedDetails),
      })}
      serpDetails={getSerpDetailsForPreview({
        draft,
        location,
        categories: suggestedCategories,
        brand,
        hirerAccount,
        isJobFeatured: Boolean(
          getProductFeatureByType({
            featureType: 'SponsoredSlot',
            productFeatures: cartFeatures,
          }),
        ),
        isBrandingEnabled: Boolean(
          getProductFeatureByType({
            featureType: 'Branding',
            productFeatures: cartFeatures,
          }),
        ),
        isKeySellingPointsEnabled: Boolean(
          getProductFeatureByType({
            featureType: 'BulletPoints',
            productFeatures: cartFeatures,
          }),
        ),
        isUrgentHiringEnabled: Boolean(
          getProductFeatureByType({
            featureType: 'DisplayTag',
            productFeatures: cartFeatures,
          }),
        ),
        translatedLabels: {
          urgentHiringLabel: isANZCountryCode(location?.country || '')
            ? t('immediate-start-label')
            : t('urgent-tag-label'),
        },
      })}
    />
  );
};

interface PreviewSectionViewProps {
  jobDetails: ComponentProps<typeof JobDetailsView>['job'] | null;
  serpDetails: ComponentProps<typeof SearchResultPreview>['serpDetails'] | null;
  loading?: boolean;
  handleTabClick: (index: number) => void;
  handlePrintClick: (index: number) => void;
}

const PreviewLoading: React.FC = () => (
  <Box
    width="full"
    alignItems="center"
    justifyContent="center"
    style={{ display: 'flex' }}
  >
    <Loader />
  </Box>
);

export const PreviewSectionView: React.FC<PreviewSectionViewProps> = ({
  loading,
  jobDetails,
  serpDetails,
  handlePrintClick,
  handleTabClick,
}) => {
  if (loading) {
    return <PreviewLoading />;
  }

  return (
    <ErrorBoundary
      onError={(error, info) => {
        log.error('Preview section | Failed to render', { error, info });
      }}
      fallback={<PreviewLoading />}
    >
      <JobPreview
        jobDetails={jobDetails}
        serpDetails={serpDetails}
        onPrintClick={handlePrintClick}
        onTabClick={handleTabClick}
      />
    </ErrorBoundary>
  );
};
